import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Icon } from './Icon';
import { Globe, ChevronDown, X } from 'lucide-react';
import { getLanguageMeta, getLanguageFlag, getLanguageName, getLanguageCode } from '../constants/languages';
import { Z } from '../constants/tokens';

/**
 * Unified language selector: Globe icon + dropdown + flag.
 *
 * Props:
 *  - languages: string[]  — available language codes (e.g. ['en','sv','he'])
 *  - value: string        — currently selected code (e.g. 'en')
 *  - onChange: (code) => void
 *  - theme: object        — app theme
 *  - compact: boolean     — smaller size for tight spaces (default false)
 *  - showOff: boolean     — include "Off" option (for subtitles, default false)
 *  - offLabel: string     — label for off option (default 'Off')
 *  - clearable: boolean   — show X to clear selection (default false)
 *  - label: string        — optional label text (e.g. 'Subtitles')
 *  - placeholder: string  — text when nothing selected
 */
export default function LanguageSelector({
  languages = [],
  value,
  onChange,
  theme,
  compact = false,
  showOff = false,
  offLabel = 'Off',
  clearable = false,
  label,
  placeholder = 'Select language',
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const sorted = useMemo(() => {
    return [...languages]
      .map(code => ({
        code,
        name: getLanguageName(code),
        flag: getLanguageFlag(code),
        meta: getLanguageMeta(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [languages]);

  const selected = value && value !== 'off'
    ? sorted.find(l => l.code === value) || { code: value, name: getLanguageName(value), flag: getLanguageFlag(value), meta: getLanguageMeta(value) }
    : null;

  const isOff = value === 'off';
  const fontSize = compact ? '0.68rem' : '0.75rem';
  const flagSize = compact ? '14px' : '16px';
  const iconSize = compact ? 13 : 15;
  const pad = compact ? '3px 8px' : '5px 10px';

  return (
    <div
      ref={ref}
      // Escape closes the menu. Scoped to this subtree (not document) so it
      // only fires when focus is actually inside the selector, and stopped so
      // an enclosing modal doesn't close at the same time.
      onKeyDown={(e) => {
        if (!open) return;
        if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
      }}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: compact ? '4px' : '6px' }}
    >
      {label && (
        <span style={{
          fontSize: compact ? '0.55rem' : '0.6rem', fontWeight: 700,
          color: theme.textSub, letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          {label}
        </span>
      )}

      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: compact ? '4px' : '6px',
          padding: pad, borderRadius: '8px',
          border: `1px solid ${open ? (theme.glossaryAccent || '#3b82f6') : theme.border}`,
          background: theme.inputBg || theme.card,
          color: theme.text, cursor: 'pointer',
          fontSize, fontFamily: 'inherit', fontWeight: 600,
          transition: 'border-color 0.15s',
          minWidth: compact ? '90px' : '110px',
        }}
      >
        {/* The per-language accent (meta.text) is a badge colour meant to sit on
            its own tinted meta.bg pill — as foreground on the plain input
            surface the warm ones (Portuguese #eab308, Spanish #f59e0b, Italian
            #fb923c) drop under 2:1 on light themes. Language identity is still
            carried by the flag here and by the code chip in the menu. */}
        <Icon icon={Globe} size={iconSize} color={selected ? (theme.glossaryAccent || theme.text) : theme.textSub} />
        {selected ? (
          <>
            <span style={{ fontSize: flagSize, lineHeight: 1 }}>{selected.flag}</span>
            <span style={{ color: theme.text }}>{selected.name}</span>
          </>
        ) : isOff ? (
          <span style={{ color: theme.textSub }}>{offLabel}</span>
        ) : (
          <span style={{ color: theme.textSub }}>{placeholder}</span>
        )}
        <Icon icon={ChevronDown} size={10} color={theme.textSub} style={{ marginLeft: 'auto' }} />
      </button>

      {/* Flag display (right of button when selected) */}
      {clearable && selected && (
        <button
          onClick={() => onChange('')}
          title="Clear language"
          aria-label="Clear language"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: theme.textSub, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            // The hit area is grown with padding and then clawed back with an
            // equal negative margin, so the touch target is bigger than the
            // 12px icon WITHOUT widening the row. In compact mode this matters:
            // the under-video usage sits in a fixed row beside the timecode and
            // zoom controls, and a plain 32px box pushed that row out of shape.
            // The negative margin stays under the flex gap (4px compact / 6px)
            // so it never overlaps the trigger button next to it.
            padding: compact ? '5px' : '8px',
            margin: compact ? '-3px' : '-5px',
            minWidth: compact ? '22px' : '28px',
            minHeight: compact ? '22px' : '28px',
          }}
        >
          <Icon icon={X} size={12} />
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: Z.dropdown }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: '4px',
            zIndex: Z.dropdown, minWidth: '180px', maxHeight: '280px', overflowY: 'auto',
            borderRadius: '10px', padding: '4px',
            background: theme.sidebar || '#fff',
            border: `1px solid ${theme.border || '#e5e7eb'}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}>
            {showOff && (
              <button
                onClick={() => { onChange('off'); setOpen(false); }}
                aria-pressed={isOff}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 10px', borderRadius: '6px',
                  border: 'none', background: isOff ? `${theme.glossaryAccent}15` : 'transparent',
                  cursor: 'pointer', fontSize, color: theme.textSub, fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={e => { if (!isOff) e.currentTarget.style.background = theme.card; }}
                onMouseLeave={e => { if (!isOff) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: flagSize, textAlign: 'center', opacity: 0.4 }}>-</span>
                <span>{offLabel}</span>
              </button>
            )}
            {sorted.map(lang => {
              const isActive = lang.code === value;
              return (
                <button
                  key={lang.code}
                  onClick={() => { onChange(lang.code); setOpen(false); }}
                  aria-pressed={isActive}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '6px 10px', borderRadius: '6px',
                    border: 'none',
                    // Accent stays in the tint + the code chip; the label keeps
                    // theme.text so warm accents don't fall below AA on light.
                    background: isActive ? `${lang.meta?.text || theme.glossaryAccent}15` : 'transparent',
                    cursor: 'pointer', fontSize, fontFamily: 'inherit',
                    color: theme.text,
                    fontWeight: isActive ? 700 : 400,
                    textAlign: 'left',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = theme.card; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: flagSize, lineHeight: 1 }}>{lang.flag}</span>
                  <span style={{ flex: 1 }}>{lang.name}</span>
                  <span style={{
                    fontSize: '0.58rem', fontWeight: 700,
                    color: lang.meta?.text || theme.textSub,
                    background: lang.meta?.bg || 'transparent',
                    border: `1px solid ${lang.meta?.border || 'transparent'}`,
                    padding: '1px 5px', borderRadius: '4px',
                  }}>
                    {getLanguageCode(lang.code)}
                  </span>
                </button>
              );
            })}
            {sorted.length === 0 && (
              <div style={{ padding: '10px', textAlign: 'center', fontSize: '0.72rem', color: theme.textSub }}>
                No languages available
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
